/* Branches — listing within a city radius, single-branch detail.
 * Backed by T-103 + T-203 (serviceability) + the customer-PWA infill
 * (GET /branches, GET /branches/{id}).
 *
 * Wire contract:
 *   - `GET /branches?vertical=&city_id=&lat=&lng=` returns `PublicBranch[]`,
 *     ordered nearest-first when lat/lng supplied. Each row carries
 *     `supplier_display_name`, `supplier_vertical`, and an optional
 *     `distance_meters` (only present when origin supplied).
 *   - `GET /branches/{id}` returns one `PublicBranch`. Only active branches
 *     under active suppliers surface.
 *   - Snake → camel translation here so screens read `.distanceMeters` etc.
 *
 * Endpoints that do NOT exist on the backend yet (preserved as mock-only
 * for now; flagged as follow-ups):
 *   - `GET /branches/featured`  → needs `is_featured`/`featured_rank` column
 *     + admin curation surface. Until then this falls back to mocks even
 *     when API_BASE_URL is set, so the Home rail doesn't 404.
 *   - `GET /branches/{id}/reviews` + POST  → reviews aren't in the v1
 *     schema. Same fallback behaviour.
 */

import { api, mockOr } from '../lib/api.js';
import { config } from '../lib/config.js';
import { D } from '../mock-data.js';

function toCamelBranch(b) {
  return {
    id: b.id,
    name: b.name,
    supplierId: b.supplier_id,
    supplierName: b.supplier_display_name,
    vertical: b.supplier_vertical,
    cityId: b.city_id,
    pincode: b.pincode,
    lat: b.lat,
    lng: b.lng,
    distanceMeters: b.distance_meters,
  };
}

export async function listBranches({ vertical, lat, lng, cityId } = {}) {
  return mockOr(
    async () => {
      const rows = await api.get('/branches', {
        query: { vertical, lat, lng, city_id: cityId },
      });
      return (rows || []).map(toCamelBranch);
    },
    () =>
      vertical === 'salon'
        ? D.branches.salons
        : vertical === 'grocery'
          ? D.branches.grocery
          : [...D.branches.grocery, ...D.branches.salons],
  );
}

/**
 * Editorial featured stores. Backend endpoint NOT yet built — until the
 * `is_featured` schema add lands, this stays mock-only even when API_BASE_URL
 * is set. Returns [] from real backend → Home rail hides itself.
 */
export async function listFeaturedBranches({ limit = 6 } = {}) {
  if (!config.IS_MOCK) {
    // Backend endpoint pending; return empty so the Home row collapses.
    return [];
  }
  return [...D.branches.grocery, ...D.branches.salons]
    .slice()
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}

export async function getBranch(branchId) {
  return mockOr(
    async () => toCamelBranch(await api.get(`/branches/${branchId}`)),
    () => {
      const b = [...D.branches.grocery, ...D.branches.salons].find((x) => x.id === branchId);
      if (!b) throw new Error('branch not found');
      return b;
    },
  );
}

/** Reviews — schema gap. No backend table yet. Returns mock-only data even
 *  in non-mock mode so the UI section renders empty rather than 404. */
export async function listBranchReviews(branchId) {
  if (!config.IS_MOCK) return [];
  return D.branchReviews[branchId] || [];
}

/** Same caveat as listBranchReviews — reviews table doesn't exist yet. */
export async function submitReview(branchId, { rating, text, productTag } = {}) {
  if (!config.IS_MOCK) {
    // Be loud — submitting a review against a real backend without the
    // schema in place is a wasted intent. Surface to caller.
    throw Object.assign(new Error('Reviews are not yet supported on this server.'), {
      code: 'not_implemented',
      status: 501,
    });
  }
  return { id: 'r-mock-' + Date.now(), rating, text, productTag };
}
